import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookOpen, CheckCircle2, Loader2, Award, Hammer, Lock, Crown, Wrench, Swords } from "lucide-react";
import { LIVELLO } from "@/lib/labels";

const LIBRARY = "https://images.unsplash.com/photo-1531259736756-6caccf485f81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxvbGQlMjBsaWJyYXJ5JTIwYm9va3MlMjB1bml2ZXJzaXR5fGVufDB8fHx8MTc4Mzc4NjA2NHww&ixlib=rb-4.1.0&q=85";

function CourseCard({ c, onClick, officina }) {
  const pct = c.n_argomenti ? Math.round((c.lezioni_completate / c.n_argomenti) * 100) : 0;
  return (
    <button
      onClick={onClick}
      data-testid={`course-card-${c.id}`}
      className={`text-left group rounded-md p-5 transition-all hover:-translate-y-1 ${
        officina
          ? "bg-primary/[0.04] border border-primary/30 hover:border-primary/60 hover:shadow-[0_8px_30px_-12px_rgba(114,47,55,0.35)]"
          : "bg-card border border-border hover:border-primary/40 hover:shadow-[0_8px_30px_-12px_rgba(114,47,55,0.25)]"
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-sm transition-colors ${officina ? "bg-primary/15 group-hover:bg-primary/25" : "bg-secondary group-hover:bg-primary/10"}`}>
          {officina
            ? <Wrench className="h-5 w-5 text-primary" strokeWidth={1.5} />
            : <BookOpen className="h-5 w-5 text-primary" strokeWidth={1.5} />}
        </div>
        <div className="flex items-center gap-2">
          {c.superato && (
            <Badge className={`rounded-full ${(c.esame?.a_regola_arte ?? c.esame?.lode) ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground"}`}>
              {c.esame?.esito || c.esame?.grado}
            </Badge>
          )}
          <Badge variant="outline" className="rounded-full border-border text-muted-foreground">{c.cfu} ⭐ stelle</Badge>
        </div>
      </div>
      <h3 className="font-serif text-lg font-bold leading-snug mb-1">{c.nome}</h3>
      <p className="text-xs text-muted-foreground mb-4">Maestro: {c.docente}</p>
      <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-2">{c.descrizione}</p>

      <div className="flex items-center gap-2 mb-1.5">
        <Progress value={pct} className="h-1.5" />
        <span className="text-xs text-muted-foreground tabular-nums w-9 text-right">{pct}%</span>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {c.superato ? (
          <><Award className="h-3.5 w-3.5 text-accent" strokeWidth={1.5} /> Prova superata</>
        ) : (
          <><CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} /> {c.lezioni_completate}/{c.n_argomenti} lectiones</>
        )}
      </div>
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/curriculum").then((res) => setData(res.data));
  }, []);

  if (!data)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 animate-spin text-primary" strokeWidth={1.5} />
        </div>
      </div>
    );

  const anni = [1, 2, 3];
  const superati = data.corsi.filter((c) => c.superato);
  const cfuAcq = superati.reduce((s, c) => s + c.cfu, 0);

  return (
    <div className="min-h-screen">
      <Navbar />

      {!user?.has_access && (
        <div className="bg-primary text-primary-foreground" data-testid="paywall-banner">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm flex items-center gap-2">
              <Lock className="h-4 w-4" strokeWidth={1.5} />
              La Bottega è aperta — prendi il tuo posto per sbloccare tutti i livelli e le Prove.
            </p>
            <Link to="/abbonamento">
              <Button size="sm" variant="secondary" className="rounded-full h-8" data-testid="paywall-subscribe-btn">
                <Crown className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Prendi il tuo posto
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Welcome banner */}
      <div className="relative border-b border-border overflow-hidden">
        <img src={LIBRARY} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/70" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-12">
          <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-2">Percorso in 3 livelli</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold mb-3">Il tuo percorso in Bottega, {user?.name?.split(" ")[0]}</h1>
          <p className="text-muted-foreground max-w-xl mb-6">
            Impara dai Maestri, supera le Prove e fai crescere la tua Maestria attraverso i tre livelli della Bottega.
          </p>
          <div className="flex flex-wrap gap-3">
            <div className="bg-card border border-border rounded-md px-4 py-3">
              <p className="text-2xl font-serif font-bold text-primary">{cfuAcq}<span className="text-muted-foreground text-base">/{data.cfu_totali}</span></p>
              <p className="text-xs text-muted-foreground">Stelle acquisite</p>
            </div>
            <div className="bg-card border border-border rounded-md px-4 py-3">
              <p className="text-2xl font-serif font-bold text-primary">{superati.length}<span className="text-muted-foreground text-base">/{data.corsi.length}</span></p>
              <p className="text-xs text-muted-foreground">Prove superate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Study plan */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 space-y-12">
        {anni.map((anno) => (
          <section key={anno}>
            <div className="flex items-center gap-3 mb-5">
              <Hammer className="h-5 w-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-serif text-2xl font-bold">{LIVELLO[anno]}</h2>
              <span className="text-xs text-muted-foreground">Livello {anno}</span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.corsi.filter((c) => c.anno === anno && !c.speciale).map((c) => (
                <CourseCard key={c.id} c={c} onClick={() => navigate(`/corso/${c.id}`)} />
              ))}
            </div>
          </section>
        ))}

        {data.corsi.filter((c) => c.speciale).map((c) => (
          <section key={c.id} data-testid={`special-section-${c.id}`} className="relative rounded-lg border border-accent/40 bg-accent/[0.04] p-6 sm:p-8">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <Swords className="h-5 w-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-serif text-2xl font-bold">{c.nome}</h2>
              <Badge className="rounded-full bg-accent text-accent-foreground">Percorso speciale</Badge>
              <span className="text-xs text-accent-foreground/80">Arbitro: {c.docente}</span>
              <div className="h-px flex-1 bg-accent/30" />
            </div>
            <p className="text-sm text-muted-foreground max-w-2xl mb-5">{c.descrizione}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <CourseCard c={c} officina onClick={() => navigate(`/corso/${c.id}`)} />
            </div>
          </section>
        ))}

        {data.corsi.some((c) => c.anno === 4) && (
          <section data-testid="officina-section" className="relative rounded-lg border border-primary/30 bg-primary/[0.03] p-6 sm:p-8">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <Wrench className="h-5 w-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-serif text-2xl font-bold">L'Officina del Vivere</h2>
              <Badge className="rounded-full bg-primary text-primary-foreground">Percorso trasversale</Badge>
              <span className="text-xs text-accent-foreground/80">Accessibile da subito · nessun prerequisito</span>
              <div className="h-px flex-1 bg-primary/20" />
            </div>
            <p className="text-sm text-muted-foreground max-w-2xl mb-5">
              Filosofia applicata ai dilemmi di oggi. Un percorso pratico che puoi affrontare da subito,
              in parallelo ai livelli della Bottega.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {data.corsi.filter((c) => c.anno === 4).map((c) => (
                <CourseCard key={c.id} c={c} officina onClick={() => navigate(`/corso/${c.id}`)} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
